import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import Turnstile from "../components/Turnstile";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [captchaToken, setCaptchaToken] = useState("");

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [searchParams]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, referral_code: form.referral_code || null, captcha_token: captchaToken });
      toast.success("Account created — welcome to Fx Cloud!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell min-h-screen grid lg:grid-cols-2 bg-fx-base">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg bg-brandblue grid place-items-center"><TrendingUp className="h-5 w-5" /></div>
            <span className="font-display font-extrabold text-xl">Fx Cloud</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Create account</h1>
          <p className="text-fx-s3 mt-2 text-sm">Start with $10,000 in virtual demo funds instantly.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name-input" required value={form.name} onChange={upd("name")} placeholder="Jane Trader" className="h-11 bg-fx-s1 border-white/10" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email-input" type="email" required value={form.email} onChange={upd("email")} placeholder="you@example.com" className="h-11 bg-fx-s1 border-white/10" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password-input" type="password" required value={form.password} onChange={upd("password")} placeholder="Min. 6 characters" className="h-11 bg-fx-s1 border-white/10" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ref">Referral code <span className="text-fx-s3">(optional)</span></Label>
              <Input id="ref" data-testid="register-referral-input" value={form.referral_code} onChange={upd("referral_code")} placeholder="e.g. AB12CD34" className="h-11 bg-fx-s1 border-white/10" />
            </div>
            <Turnstile onToken={setCaptchaToken} />
            <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full h-11 bg-brandblue hover:bg-blue-500 text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </form>


          <p className="text-center text-sm text-fx-s3 mt-8">
            Already registered?{" "}
            <Link to="/login" data-testid="go-login-link" className="text-brandblue hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-l border-white/5 order-1 lg:order-2">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "url(https://images.pexels.com/photos/8386437/pexels-photo-8386437.jpeg?auto=compress&cs=tinysrgb&w=1200)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-fx-base via-fx-base/70 to-transparent" />
        <div className="relative flex items-center gap-2 justify-end text-white">
          <span className="font-display font-extrabold text-xl tracking-tight">Fx Cloud</span>
          <div className="h-9 w-9 rounded-lg bg-brandblue grid place-items-center"><TrendingUp className="h-5 w-5" /></div>
        </div>
        <div className="relative text-right ml-auto max-w-md">
          <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight">AI bots.<br />Real workflow.<br />Zero risk to learn.</h2>
        </div>
      </div>
    </div>
  );
}
